import type { ShipstationOrder } from '../../types/apiTypes';
import { NAME_SEARCH_MAX_RESULTS } from '../../constants/app.constants';
import { collectBundleParentSkusFromItems } from './orderdetail/orderDataLogic';

export interface OrderStatusFlairs {
  holdOrder: boolean;
  clearToFulfill: boolean;
}

export function getOrderStatusFlairs(
  order: ShipstationOrder,
  tagDictionary: Record<string, string | number>,
): OrderStatusFlairs {
  const tagIds = order.tagIds ?? [];
  const clearToFulfill = tagIds.includes(tagDictionary['Cleared for fulfillment'] as number);
  const holdOrder =
    tagIds.includes(tagDictionary['Check Shopify'] as number) ||
    tagIds.includes(tagDictionary['Check Slack'] as number) ||
    tagIds.includes(tagDictionary['Hold for CS'] as number);

  return { holdOrder, clearToFulfill };
}

export function filterOrdersHidingIssues(
  orders: readonly ShipstationOrder[],
  tagDictionary: Record<string, string | number>,
  hideIssues: boolean,
): readonly ShipstationOrder[] {
  if (!hideIssues) {
    return orders;
  }
  return orders.filter((order) => !getOrderStatusFlairs(order, tagDictionary).holdOrder);
}

/** Sum of line quantities, excluding parent bundle SKUs reconstructed from sibling `sb bundle title` options. */
export function getOrderItemCount(order: ShipstationOrder): number {
  if (!order.items?.length) {
    return 0;
  }
  const parentSkus = collectBundleParentSkusFromItems(order.items);
  return order.items.reduce((sum, item) => {
    const sku = item.sku?.trim().toUpperCase();
    if (sku && parentSkus.has(sku)) {
      return sum;
    }
    return sum + (item.quantity ?? 1);
  }, 0);
}

function orderMatchesQuery(order: ShipstationOrder, normalizedQuery: string): boolean {
  const orderNumber = order.orderNumber != null ? String(order.orderNumber).toLowerCase() : '';
  if (orderNumber.includes(normalizedQuery)) {
    return true;
  }
  const shipToName = order.shipTo?.name?.toLowerCase() ?? '';
  if (shipToName.includes(normalizedQuery)) {
    return true;
  }
  const customerName = order.customerName?.toLowerCase() ?? '';
  return customerName.includes(normalizedQuery);
}

export function matchOrdersForSearch(
  orders: readonly ShipstationOrder[],
  query: string,
  maxResults: number = NAME_SEARCH_MAX_RESULTS,
): ShipstationOrder[] {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) {
    return [];
  }
  const matches: ShipstationOrder[] = [];
  for (const order of orders) {
    if (orderMatchesQuery(order, trimmed)) {
      matches.push(order);
      if (matches.length >= maxResults) {
        break;
      }
    }
  }
  return matches;
}

export function formatOrderSearchLabel(order: ShipstationOrder): string {
  const orderNumber = order.orderNumber != null ? String(order.orderNumber) : '';
  const customerName = order.shipTo?.name ?? order.customerName ?? '';
  const itemCount = getOrderItemCount(order);
  return `${orderNumber} - ${customerName} - ${itemCount} items.`;
}
